/**
 * Modal engine adapter — the one Dependency-Inversion seam over the modal engine.
 *
 * Higher layers (the controller, the behaviour modules, feature hooks) depend on
 * the `ModalEngine` interface and NEVER call jQuery `.modal()` /
 * `window.bootstrap.Modal` / eModal directly. This is the single file that knows
 * which engine is live, so a BS4→BS5 migration is a diff confined here (plus the
 * partial's attribute names). It consolidates the jQuery/BS5 probing that
 * previously lived duplicated in `modal-history-back.ts` and
 * `model-finder-page-modal.ts`.
 *
 * Engine selection happens at call time (not at module load) so the adapter
 * works whether jQuery/Bootstrap loads before or after this bundle:
 *   - jQuery present          → BS4 path (`jq(el).modal(...)` + `.on('*.bs.modal')`)
 *   - `window.bootstrap.Modal`→ BS5 path (`getOrCreateInstance` + native events)
 *   - neither                 → trigger-click fallback (the pre-existing behaviour)
 *
 * BS4 is the active path; the BS5 branch is present but dormant until the
 * migration (target §3.4, §N.4).
 *
 * eModal stays its own concern — eModal-built shells (alerts/confirms/quickView)
 * are brought onto the unified stack by the runtime upgrade adapter
 * (`modal-upgrade.ts`), not by this engine. (#5's compact cart modal no longer
 * uses eModal/`ModalManager`; it is theme-owned via `datte_modal.tpl` — see
 * target §3.5 + ADR-0008 Decision-History.) The controller does not own AJAX modals.
 *
 * @see docs/architecture/modals/target-architecture.md §3.4 (engine adapter)
 * @see docs/architecture/modals/shop-foundations.md §4.5 (engine-probe discipline, G5/G10)
 */

import type { Unbind } from '../utils/unbind';

export type { Unbind };

/** The lifecycle events every modal emits; named to match Bootstrap's `*.bs.modal`. */
type ModalLifecycle = 'show' | 'shown' | 'hide' | 'hidden';

export interface ModalEngine {
  open(modal: HTMLElement): void;
  close(modal: HTMLElement): void;
  onShow(modal: HTMLElement, cb: () => void): Unbind;
  onShown(modal: HTMLElement, cb: () => void): Unbind;
  onHide(modal: HTMLElement, cb: () => void): Unbind;
  onHidden(modal: HTMLElement, cb: () => void): Unbind;
}

// --- engine probes (the ONLY place these globals are read) --------------------

/** Minimal jQuery surface this adapter needs — `.modal()` + `.on/.off`. */
interface JqInstance {
  modal(action: 'show' | 'hide'): void;
  on(event: string, handler: () => void): JqInstance;
  off(event: string, handler: () => void): JqInstance;
}
type JqFactory = (el: HTMLElement) => JqInstance;

function getJq(): JqFactory | undefined {
  // JTL declares window.$/jQuery loosely; read via a narrow local type.
  const win = window as Window & { jQuery?: JqFactory; $?: JqFactory };
  return win.jQuery ?? win.$;
}

interface Bs5ModalInstance {
  show(): void;
  hide(): void;
}
interface Bs5ModalCtor {
  getOrCreateInstance?(el: HTMLElement): Bs5ModalInstance;
  getInstance?(el: HTMLElement): Bs5ModalInstance | null;
}
function getBs5(): Bs5ModalCtor | undefined {
  return (window as Window & { bootstrap?: { Modal?: Bs5ModalCtor } }).bootstrap?.Modal;
}

// --- the default engine -------------------------------------------------------

/**
 * Subscribe to a lifecycle event. BS4 emits jQuery events (`show.bs.modal`),
 * BS5 emits native DOM events of the same name — both are covered by binding the
 * native listener AND the jQuery listener when jQuery is present (BS4 dispatches
 * through jQuery, so the native path alone would miss it). Returns an unbind.
 */
function subscribe(modal: HTMLElement, phase: ModalLifecycle, cb: () => void): Unbind {
  const event = `${phase}.bs.modal`;
  const jq = getJq();
  if (jq) {
    jq(modal).on(event, cb);
    return () => {
      jq(modal).off(event, cb);
    };
  }
  modal.addEventListener(event, cb);
  return () => modal.removeEventListener(event, cb);
}

class DefaultModalEngine implements ModalEngine {
  public open(modal: HTMLElement): void {
    const jq = getJq();
    if (jq) {
      jq(modal).modal('show');
      return;
    }
    const inst = getBs5()?.getOrCreateInstance?.(modal);
    if (inst) {
      inst.show();
      return;
    }
    // No engine yet — fall back to clicking the trigger that targets this modal.
    document
      .querySelector<HTMLElement>(`[data-target="#${modal.id}"], [data-bs-target="#${modal.id}"]`)
      ?.click();
  }

  public close(modal: HTMLElement): void {
    // Blur a focused descendant first to avoid the aria-hidden focus conflict
    // (Bootstrap moves focus out of the modal on hide).
    if (document.activeElement instanceof HTMLElement && modal.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    const jq = getJq();
    if (jq) {
      jq(modal).modal('hide');
      return;
    }
    const inst = getBs5()?.getInstance?.(modal);
    if (inst) {
      inst.hide();
      return;
    }
    modal
      .querySelector<HTMLButtonElement>('[data-dismiss="modal"], [data-bs-dismiss="modal"]')
      ?.click();
  }

  public onShow(modal: HTMLElement, cb: () => void): Unbind {
    return subscribe(modal, 'show', cb);
  }
  public onShown(modal: HTMLElement, cb: () => void): Unbind {
    return subscribe(modal, 'shown', cb);
  }
  public onHide(modal: HTMLElement, cb: () => void): Unbind {
    return subscribe(modal, 'hide', cb);
  }
  public onHidden(modal: HTMLElement, cb: () => void): Unbind {
    return subscribe(modal, 'hidden', cb);
  }
}

/** The default engine instance — picks the engine at call time (see module header). */
export const modalEngine: ModalEngine = new DefaultModalEngine();
